/**
 * FetchPlaylistUseCase Implementation
 *
 * Orchestrates playlist fetching through the PlaylistGateway.
 * Use-cases coordinate entities and gateways but don't implement business rules.
 *
 * Clean Architecture:
 * - usecase-orchestrates-not-implements: Delegates to gateway
 * - usecase-single-responsibility: Only handles fetch orchestration
 * - usecase-explicit-dependencies: Gateway injected via constructor
 */

import type {
  FetchPlaylistUseCase,
  FetchPlaylistInput,
  FetchPlaylistOutput,
} from '@/domain/use-cases';
import type { PlaylistGateway } from '@/domain/gateways';
import { getTrackCount } from '@/domain/entities';
import { Logger } from '@/app/utils/logger';

const LOG_TAG = 'fetch-playlist-usecase';

/**
 * Implementation of FetchPlaylistUseCase.
 *
 * Fetches playlist through the injected gateway and returns
 * the result in a use-case-specific output format.
 */
export class FetchPlaylistUseCaseImpl implements FetchPlaylistUseCase {
  constructor(private readonly playlistGateway: PlaylistGateway) {}

  /**
   * Execute the use-case to fetch the playlist.
   *
   * @param input - Optional input with forceRefresh flag
   * @returns Promise with playlist, source, and staleness info
   */
  async execute(input?: FetchPlaylistInput): Promise<FetchPlaylistOutput> {
    const forceRefresh = input?.forceRefresh ?? false;

    try {
      Logger.info(`Executing FetchPlaylistUseCase (forceRefresh=${forceRefresh})`, LOG_TAG);

      const result = await this.playlistGateway.fetchPlaylist({
        forceRefresh,
      });

      const trackCount = getTrackCount(result.playlist);
      Logger.info(
        `Playlist fetched: ${trackCount} tracks, source=${result.source}, stale=${result.isStale}`,
        LOG_TAG
      );

      return {
        playlist: result.playlist,
        source: result.source,
        isStale: result.isStale,
      };
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      Logger.error(`FetchPlaylistUseCase failed: ${message}`, LOG_TAG);
      throw error;
    }
  }
}

/**
 * Factory function to create FetchPlaylistUseCaseImpl with gateway.
 *
 * @param playlistGateway - Gateway for playlist operations
 * @returns Configured FetchPlaylistUseCase instance
 */
export function createFetchPlaylistUseCase(
  playlistGateway: PlaylistGateway
): FetchPlaylistUseCase {
  return new FetchPlaylistUseCaseImpl(playlistGateway);
}
